import _ from "underscore";
import DriveView from "../../DriveView";
import { dispatcher, DispatcherEvents } from "../../utils/dispatcher";
import Sync from "../../sync/sync";
import { generateChecklist, ListView } from "../framework/checklist";
import { Shipment, IShipment } from "../../model/Shipment";
import constants from "../../constants";
import { getActiveShipments } from "../../model/Shipments";
import { getShipmentsFromState, setShipmentsInState } from "./helper";
import { CancelablePromise as Promise } from "../../utils/cancelablePromise";
import { handleApi } from "../../utils/error";

import pageHTML from "./manageShipments.tmpl";
import SHIPMENT_TEMPLATE from "./manageShipmentsListItem.tmpl";
import PerformanceProfilerFactory from "../../utils/performanceProfiler/performanceProfilerFactory";
import { MeasureName } from "../../utils/performanceProfiler/measureName";

interface IShipmentViewLog extends IShipment {
    removeLabel: string;
}

export default class ManageShipmentsView extends DriveView<Backbone.Model> {

    protected strings = [
        "Remove {0} shipment"
    ];
    private readonly sync: Sync;
    private readonly workflow: boolean;
    private readonly template = _.template(pageHTML);
    private listView: ListView;
    private shipments: Shipment[];

    constructor (options) {
        super(options);
        this.sync = this.app.sync;
        this.workflow = options.workflow || false;

        this.fetchShipments();
        this.listenTo(dispatcher, DispatcherEvents.SyncShipments, () => this.refresh());
    }

    /* eslint-disable @typescript-eslint/unbound-method */
    events() {
        return {
            "click .removeShipment": this.removeShipment
        } as Backbone.EventsHash;
    }
    /* eslint-disable @typescript-eslint/unbound-method */

    refresh(render = true) {
        this.fetchShipments();
        render && this.render();
    }

    render() {
        let hasShipments = !!this.shipments.length;
        Promise.all([
            this.display(this.template({ hasShipments })),
            this.translate()
        ])
        .then(() => {
            this.listView && this.listView.remove();
            let listContainer = document.getElementById("shipments-list");
            if (hasShipments && listContainer) {
                this.listView = generateChecklist({
                    items: this.shipments
                        .slice(0, constants.ASSET_LIMITS.MAX_SHIPMENTS_LIMIT)
                        .map(this.getShipmentViewLog),
                    labelTemplate: SHIPMENT_TEMPLATE,
                    isCheckable: false,
                    context: this,
                    translator: this.translator,
                    el: listContainer
                });
            }
        })
        .catch(handleApi);
        return this;
    }

    getCurrentShipments() {
        return this.shipments || [];
    }

    remove () {
        this.listView && this.listView.remove();
        super.remove();
        return this;
    }

    private fetchShipments() {
        let totalActiveShipments = getActiveShipments(this.app.userService, this.app.store);
        this.shipments = totalActiveShipments.map(s => new Shipment(s));
    }

    private syncOrRemoveShipment(shipment: Shipment) {
        let removeShipmentProfiler = PerformanceProfilerFactory.getInstance(MeasureName.RemoveShipment);
        if (!shipment._isTemp) {
            return removeShipmentProfiler.measureAsync(() => this.sync.shipments.setItem(shipment, this.workflow));
        }
        removeShipmentProfiler.start();
        let tempShipments = getShipmentsFromState(this.app.store, this.app.userService);
        let removedId = shipment.id;
        tempShipments = tempShipments.filter(tempShipment => tempShipment.id !== removedId);
        setShipmentsInState(this.app.store, tempShipments, this.app.userService);
        removeShipmentProfiler.end();
        return Promise.resolve();
    }

    private removeShipment(e) {
        let id = e.currentTarget.getAttribute("data-shipment");
        let removedShipment = this.shipments.filter(shipment => shipment.id === id)[0];
        removedShipment.detach();
        this.shipments = this.shipments.filter(shipment => shipment.id !== id);
        this.render();
        this.syncOrRemoveShipment(removedShipment).then(() => this.trigger("shipmentRemoved")).catch(handleApi);
    }

    private readonly getShipmentViewLog = (shipment: Shipment): IShipmentViewLog => {
        let shipmentObj: IShipment = shipment;
        return { ...shipmentObj, removeLabel: this.getString("Remove {0} shipment").replace("{0}", this.getShipmentName(shipmentObj)) };
    };

    private getShipmentName (shipment: IShipment) {
        return [shipment.shipperName, shipment.documentNumber, shipment.commodity].join(" ");
    }
}